import { TeamAPI } from "../../../../src/apis/TeamAPI"
import { GetStaticPropsContext } from "next"
import { Col, Row } from "react-bootstrap"
import React from "react"
import { TeamDetails } from "@components/Team/TeamDetails/TeamDetails"
import { getNavigationPropsForTeam } from "@components/Navigation/getNavigationProps"
import { SEO } from "../../../../src/types/next.js"
import moment from "moment"
import { ParsedUrlQuery } from "querystring"
import InferNextPropsType from "infer-next-props-type"
import * as NextConstants from "../../../../src/constants/next"
import { TeamMenu } from "@components/Team/TeamMenu/TeamMenu"
import { TeamComparisonStats } from "@components/Team/TeamComparisonStats"
import { getTeamCanonicalData } from "../../../../src/utils/team"
import { BookmakerBonusAPI } from "../../../../src/apis/BookmakerBonusAPI"
import { simplifyBookmakerBonus } from "../../../../src/utils/bookmakerBonus"
import { TeamBettingStatsWithParameterFlat } from "../../../../src/types/teamBettingStats"
import { NewTitleBanner } from "@components/Banners/NewTitleBanner"
import homeTeamStats from "@assets/images/title-banner/home-team-stats.png"
import { MatchBettingStats } from "@components/Match/MatchBettingStats"
import { TurnMatches } from "@components/Turn/TurnMatches"

export default function TeamBilancioPage({
  team,
  comparison,
  nextMatches,
  homeTeamBettingStats,
  awayTeamBettingStats,
}: InferNextPropsType<typeof getStaticProps>) {
  return (
    <>
      <Row className={"marginZero paddingZero"}>
        <TeamDetails team={team} />
      </Row>
      <TeamMenu team={team} selected="precedenti" teamCalciocomSlug={team.calciocomSlug!} />
      <Row className={"marginZero"}>
        <Col md={12} className={"paddingFive"}>
          <div id="hs-content" className={"hs-border-div paddingZero"}>
            <TeamComparisonStats stats={comparison} team={team} />
          </div>
        </Col>
      </Row>
      {nextMatches.length > 0 && nextMatches[0] && homeTeamBettingStats && awayTeamBettingStats && (
        <>
          <Row className="marginZero">
            <NewTitleBanner icon={homeTeamStats} title="STATISTICHE PROSSIMA PARTITA" />
          </Row>
          <Row>
            <MatchBettingStats
              match={nextMatches[0]}
              homeTeamBettingStats={homeTeamBettingStats}
              awayTeamBettingStats={awayTeamBettingStats}
              isH1={false}
              newTitleBanner={false}
            />
          </Row>
        </>
      )}
      {nextMatches.length > 0 && (
        <>
          <Row>
            <NewTitleBanner icon={homeTeamStats} title="PROSSIME PARTITE" />
          </Row>
          <Row>
            <TurnMatches matches={nextMatches} loading={false} />
          </Row>
        </>
      )}
    </>
  )
}

export async function getStaticPaths() {
  return { paths: [], fallback: "blocking" }
}

interface IParams extends ParsedUrlQuery {
  teamCalciocomSlug: string
}

export async function getStaticProps(context: GetStaticPropsContext<IParams>) {
  try {
    const { teamCalciocomSlug } = context.params!

    const [teamIds] = await Promise.all([TeamAPI.getTeamIds(teamCalciocomSlug)])

    const [team, nextMatches, comparison, navigationProps] = await Promise.all([
      TeamAPI.getTeam(teamIds.id),
      TeamAPI.getNextMatches(teamIds.id),
      TeamAPI.getComparisionStats(teamIds.id),
      getNavigationPropsForTeam(teamIds.id),
    ])

    let homeTeamBettingStats: TeamBettingStatsWithParameterFlat | undefined = undefined
    let awayTeamBettingStats: TeamBettingStatsWithParameterFlat | undefined = undefined
    const match = nextMatches[0]
    if (match) {
      ;[homeTeamBettingStats, awayTeamBettingStats] = await Promise.all([
        match.homeTeam ? await TeamAPI.getBettingStats(match.homeTeam.id, match.competitionSeasonId) : undefined,
        match.awayTeam ? await TeamAPI.getBettingStats(match.awayTeam.id, match.competitionSeasonId) : undefined,
      ])
    }

    const title = `Scontri diretti del ${team.completeName} su tutte le partite`
    const seo: SEO = {
      description: `Tutte le partite precedenti di ${team.completeName} giocate in Campionato e durante le Coppe Internazionali. Scopri le statistiche di ogni scontro diretto`,
      keywords: [
        `${team.name}`,
        "calcio",
        moment().year().toString(),
        "Rosa",
        "Calendario",
        "Club",
        "Squadra",
        "informazioni",
      ],
      ...(await getTeamCanonicalData(team, null, `/squadre/${teamCalciocomSlug}/precedenti`)),
    }

    return {
      props: {
        team,
        comparison,
        navigationProps,
        seo,
        title,
        notFound: false,
        nextMatches,
        homeTeamBettingStats: homeTeamBettingStats || null,
        awayTeamBettingStats: awayTeamBettingStats || null,
      },
      revalidate: NextConstants.REVALIDATE,
    }
  } catch (error) {
    console.error(error)
    return {
      notFound: true,
    }
  }
}
